// ── Competitive Advantage Section Editor ──────────────────────────────────────
// 2×2 grid of advantages, each with icon, title, description
// Supports add/remove/reorder

import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { uid } from '../../lib/uid';

// Available Lucide icons for advantage items
const AVAILABLE_ICONS = [
  'Shield', 'Clock', 'Award', 'Users', 'Zap', 'Target', 'TrendingUp',
  'CheckCircle', 'Star', 'Heart', 'Gem', 'Crown', 'BadgeCheck', 'Rocket',
  'Briefcase', 'Building2', 'Hammer', 'HardHat', 'Wrench', 'Tool',
];


export default function AdvantagesSection({ data, onChange }) {
  const advantages = data || [];

  const addItem = () => {
    const newItem = {
      id: uid(),
      icon: 'Star',
      title: '',
      description: '',
    };
    onChange([...advantages, newItem]);
  };

  const removeItem = (idx) => {
    const updated = [...advantages];
    updated.splice(idx, 1);
    onChange(updated);
  };

  const updateItem = (idx, field, value) => {
    const updated = [...advantages];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  const moveItem = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= advantages.length) return;
    const updated = [...advantages];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {/* Items */}
      {advantages.map((adv, idx) => (
        <div key={adv.id} className="p-3 bg-[#f8f7f4] rounded-lg border border-[#e5e4df]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#c9a84c] text-[#1a1a2e] text-xs font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <span className="text-xs font-semibold text-[#6b7280]">Advantage #{idx + 1}</span>
            </div>
            <div className="flex items-center gap-1">
              {/* Move up/down */}
              <button
                type="button"
                onClick={() => moveItem(idx, -1)}
                disabled={idx === 0}
                className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] disabled:opacity-30 transition-colors"
                title="Move up"
              >
                <GripVertical className="w-3.5 h-3.5 rotate-180" />
              </button>
              <button
                type="button"
                onClick={() => moveItem(idx, 1)}
                disabled={idx === advantages.length - 1}
                className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] disabled:opacity-30 transition-colors"
                title="Move down"
              >
                <GripVertical className="w-3.5 h-3.5" />
              </button>
              {/* Delete */}
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                title="Remove advantage"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Icon selector */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
              Icon
            </label>
            <div className="flex flex-wrap gap-1">
              {AVAILABLE_ICONS.map((iconName) => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => updateItem(idx, 'icon', iconName)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    adv.icon === iconName
                      ? 'border-[#c9a84c] bg-[#c9a84c]/10 text-[#1a1a2e] font-semibold'
                      : 'border-[#e5e4df] text-[#6b7280] hover:border-[#c9a84c]/50'
                  }`}
                >
                  {iconName}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
              Title
            </label>
            <input
              type="text"
              value={adv.title || ''}
              onChange={(e) => updateItem(idx, 'title', e.target.value)}
              placeholder="e.g. Proven Track Record"
              className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
              Description
            </label>
            <textarea
              value={adv.description || ''}
              onChange={(e) => updateItem(idx, 'description', e.target.value)}
              placeholder="Describe this competitive advantage..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
            />
          </div>
        </div>
      ))}

      {/* Add button */}
      <Button
        type="button"
        onClick={addItem}
        size="sm"
        className="w-full border border-dashed border-[#c9a84c]/40 bg-transparent text-[#c9a84c] hover:bg-[#c9a84c]/10 hover:text-[#b8973e]"
        disabled={advantages.length >= 6}
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Advantage {advantages.length >= 6 ? '(max 6)' : ''}
      </Button>
    </div>
  );
}
